import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useAutenticacaoContext } from '../../../core/contexts/AutenticacaoContext';
import { useDataHora } from '../../../core/hooks/useDataHora';
import { useMoeda } from '../../../core/hooks/useMoeda';
import { ReferenciaItem } from '../../enum/ReferenciaItem';
import { ReferenciaConta } from '../../enum/ReferenciaConta';
import { IConta } from '../../interfaces/IConta';
import { IIntervaloCaixa } from '../../interfaces/IIntervaloCaixa';

interface ConferenciaCaixaProps {
    intervalo: IIntervaloCaixa;
    onClose: () => void;
}

function agruparPorChave<T>(lista: T[], chave: (item: T) => string): Map<string, T[]> {
    const mapa = new Map<string, T[]>();
    lista.forEach(item => {
        const k = chave(item) || 'Indefinido';
        if (!mapa.has(k)) mapa.set(k, []);
        mapa.get(k)!.push(item);
    });
    return mapa;
}

export const ConferenciaCaixa = ({ intervalo, onClose }: ConferenciaCaixaProps) => {
    const { real } = useMoeda();
    const { dataHoraBr } = useDataHora();
    const { estabelecimento } = useAutenticacaoContext();

    const contas: IConta[] = intervalo.contas ?? [];

    const asNumber = (valor: unknown): number => {
        if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
        if (typeof valor === 'string') {
            // Suporta "10,50", "1.234,56", "30.50", "R$ 10,50"
            const cleaned = valor.trim().replace(/[^0-9,.-]/g, '');
            const hasComma = cleaned.includes(',');
            const hasDot = cleaned.includes('.');

            let normalized = cleaned;
            if (hasComma && hasDot) {
                // pt-BR: "." milhar + "," decimal
                normalized = cleaned.replace(/\./g, '').replace(',', '.');
            } else if (hasComma) {
                // vírgula decimal
                normalized = cleaned.replace(',', '.');
            } else {
                // apenas "." (decimal) ou apenas dígitos
                normalized = cleaned;
            }

            const parsed = parseFloat(normalized);
            return Number.isFinite(parsed) ? parsed : 0;
        }
        return 0;
    };

    // --- Entradas / Saídas ---
    const totalProdutos = useMemo(() =>
        contas.flatMap(c => c.itens ?? [])
            .filter(i => i.referenciaTipo === ReferenciaItem.produto)
            .reduce((acc, i) => acc + asNumber((i as any).valorTotal ?? 0), 0),
        [contas]);

    const totalServicos = useMemo(() =>
        contas.flatMap(c => c.itens ?? [])
            .filter(i => i.referenciaTipo === ReferenciaItem.entrega)
            .reduce((acc, i) => acc + asNumber((i as any).valorTotal ?? 0), 0),
        [contas]);

    const totalRecebidosManuais = useMemo(() =>
        contas
            .filter(c => c.referenciaTipo === ReferenciaConta.manual && c.tipo === 'receber')
            .reduce((acc, c) => acc + asNumber((c as any).valor ?? 0), 0),
        [contas]);

    const totalSaidasManuais = useMemo(() =>
        contas
            .filter(c => c.referenciaTipo === ReferenciaConta.manual && c.tipo === 'pagar')
            .reduce((acc, c) => acc + asNumber((c as any).valor ?? 0), 0),
        [contas]);

    const totalLiquido = intervalo.valorFinal ?? 0;
    const totalVendas = totalProdutos + totalServicos;
    const valorInicial = intervalo.valorInicial ?? 0;

    // --- Métodos de pagamento ---
    const metodosPagamento = useMemo(() => {
        const mapa = new Map<string, number>();
        contas.forEach(c => {
            (c.pagamentos ?? []).forEach(p => {
                const metodo = p.metodoPagamento || 'Indefinido';
                mapa.set(metodo, (mapa.get(metodo) ?? 0) + asNumber((p as any).valor ?? 0));
            });
        });
        return Array.from(mapa.entries()).map(([metodo, valor]) => ({ metodo, valor }));
    }, [contas]);

    // --- Detalhamento por método de pagamento ---
    const detalhamentoPorMetodo = useMemo(() => {
        const mapa = new Map<string, { descricao: string; dataHora: string; valor: number }[]>();
        contas.forEach(c => {
            (c.pagamentos ?? []).forEach(p => {
                const metodo = p.metodoPagamento || 'Indefinido';
                if (!mapa.has(metodo)) mapa.set(metodo, []);
                mapa.get(metodo)!.push({
                    descricao: c.descricao,
                    dataHora: c.dataHora,
                    valor: asNumber((p as any).valor ?? 0),
                });
            });
        });
        return mapa;
    }, [contas]);

    // --- Produtos vendidos (agrupados) ---
    const produtosVendidos = useMemo(() => {
        const mapa = new Map<string, { quantidade: number; valorTotal: number }>();
        contas.flatMap(c => c.itens ?? [])
            .filter(i => i.referenciaTipo === ReferenciaItem.produto || !i.referenciaTipo || i.referenciaTipo === ReferenciaItem.item)
            .forEach(i => {
                const desc = i.descricao;
                if (!mapa.has(desc)) mapa.set(desc, { quantidade: 0, valorTotal: 0 });
                const atual = mapa.get(desc)!;
                mapa.set(desc, {
                    quantidade: atual.quantidade + (i.quantidade ?? 1),
                    valorTotal: atual.valorTotal + asNumber((i as any).valorTotal ?? 0),
                });
            });
        return Array.from(mapa.entries())
            .map(([descricao, { quantidade, valorTotal }]) => ({ descricao, quantidade, valorTotal }))
            .sort((a, b) => b.quantidade - a.quantidade);
    }, [contas]);

    // --- Taxas de entrega ---
    const taxasEntrega = useMemo(() => {
        const mapa = new Map<string, { quantidade: number; valorTotal: number }>();
        contas.flatMap(c => c.itens ?? [])
            .filter(i => i.referenciaTipo === ReferenciaItem.entrega)
            .forEach(i => {
                const desc = i.descricao;
                if (!mapa.has(desc)) mapa.set(desc, { quantidade: 0, valorTotal: 0 });
                const atual = mapa.get(desc)!;
                mapa.set(desc, {
                    quantidade: atual.quantidade + (i.quantidade ?? 1),
                    valorTotal: atual.valorTotal + asNumber((i as any).valorTotal ?? 0),
                });
            });
        return Array.from(mapa.entries()).map(([descricao, { quantidade, valorTotal }]) => ({ descricao, quantidade, valorTotal }));
    }, [contas]);

    // --- Contas manuais ---
    const contasManuais = useMemo(() =>
        contas.filter(c => c.referenciaTipo === ReferenciaConta.manual),
        [contas]);

    const handleImprimir = () => {
        const style = document.createElement('style');
        style.id = 'conferencia-print-style';
        style.innerHTML = `
            @media print {
                body > *:not(.MuiDialog-root) { display: none !important; }
                .MuiBackdrop-root { display: none !important; }
                #conferencia-action-bar { display: none !important; }
                .MuiDialog-paper {
                    box-shadow: none !important;
                    margin: 0 !important;
                    max-height: none !important;
                    overflow: visible !important;
                }
                .MuiDialogContent-root {
                    overflow: visible !important;
                    padding: 16px !important;
                }
            }
        `;
        document.head.appendChild(style);
        window.print();
        document.head.removeChild(style);
    };

    const nomeEstabelecimento = estabelecimento?.nomeFantasia ?? estabelecimento?.razaoSocial ?? 'Space Food';

    return (
        <Dialog open fullScreen onClose={onClose} sx={{ zIndex: 10000 }}>
            {/* Barra de ações (não impressa) */}
            <Box
                id="conferencia-action-bar"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 3,
                    py: 1.5,
                    borderBottom: '1px solid #ddd',
                    bgcolor: 'background.paper',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                }}
            >
                <Typography variant="h6" fontWeight="bold">
                    Conferência de Caixa
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={handleImprimir}
                    >
                        Imprimir
                    </Button>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            <DialogContent sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
                {/* Conteúdo do relatório */}
                <Box>
                    {/* Cabeçalho */}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="h5" fontWeight="bold">
                            Conferência de Caixa
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            {nomeEstabelecimento}
                        </Typography>
                    </Box>

                    {/* 1. Resumo do intervalo */}
                    <Typography variant="h6" fontWeight="bold" sx={{ borderBottom: '2px solid #000', mb: 1 }}>
                        Resumo do intervalo
                    </Typography>
                    <TableContainer sx={{ mb: 3 }}>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Descrição</TableCell>
                                    <TableCell>{intervalo.descricao ?? '-'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '11px' }}>{intervalo.uuid}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Abertura</TableCell>
                                    <TableCell>{dataHoraBr(intervalo.abertoAs)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Fechamento</TableCell>
                                    <TableCell>
                                        {intervalo.fechadoAs ? dataHoraBr(intervalo.fechadoAs) : 'Em aberto'}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 2. Resumo financeiro */}
                    <Typography variant="h6" fontWeight="bold" sx={{ borderBottom: '2px solid #000', mb: 1 }}>
                        Resumo financeiro
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {/* Entradas */}
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ border: '1px solid #ccc', p: 1.5, height: '100%' }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'success.main', mb: 1 }}>
                                    Entradas (+)
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Abertura:</Typography>
                                    <Typography variant="body2">{real(valorInicial)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Produtos:</Typography>
                                    <Typography variant="body2">{real(totalProdutos)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Serviços / Entrega:</Typography>
                                    <Typography variant="body2">{real(totalServicos)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Recebidos (manuais):</Typography>
                                    <Typography variant="body2">{real(totalRecebidosManuais)}</Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Saídas */}
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ border: '1px solid #ccc', p: 1.5, height: '100%' }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'error.main', mb: 1 }}>
                                    Saídas (-)
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Saídas (manuais):</Typography>
                                    <Typography variant="body2">{real(totalSaidasManuais)}</Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Resultado */}
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ border: '1px solid #ccc', p: 1.5, height: '100%' }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                    Resultado (=)
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" fontWeight="bold">Total líquido:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{real(totalLiquido)}</Typography>
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">Total das vendas:</Typography>
                                    <Typography variant="body2">{real(totalVendas)}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* 3. Resumo por método de pagamento */}
                    <Typography variant="h6" fontWeight="bold" sx={{ borderBottom: '2px solid #000', mb: 1 }}>
                        Resumo por método de pagamento
                    </Typography>
                    {metodosPagamento.length > 0 ? (
                        <TableContainer sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Método de pagamento</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Valor (sistema)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {metodosPagamento.map(({ metodo, valor }) => (
                                        <TableRow key={metodo}>
                                            <TableCell>{metodo}</TableCell>
                                            <TableCell align="right">{real(valor)}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                                            {real(metodosPagamento.reduce((acc, m) => acc + m.valor, 0))}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Nenhum pagamento registrado
                        </Typography>
                    )}

                    {/* 4. Detalhamento por método de pagamento */}
                    <Typography variant="h6" fontWeight="bold" sx={{ borderBottom: '2px solid #000', mb: 1 }}>
                        Detalhamento dos valores recebidos
                    </Typography>
                    {detalhamentoPorMetodo.size > 0 ? (
                        <Box sx={{ mb: 3 }}>
                            {Array.from(detalhamentoPorMetodo.entries()).map(([metodo, registros]) => (
                                <Box key={metodo} sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                        {metodo}
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Data / Hora</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Valor</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {registros.map((r, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell>{r.descricao}</TableCell>
                                                        <TableCell>{dataHoraBr(r.dataHora)}</TableCell>
                                                        <TableCell align="right">{real(r.valor)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow>
                                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                                                        {real(registros.reduce((acc, r) => acc + r.valor, 0))}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Nenhum pagamento registrado
                        </Typography>
                    )}

                    {/* 5. Produtos vendidos */}
                    <Typography variant="h6" fontWeight="bold" sx={{ borderBottom: '2px solid #000', mb: 1 }}>
                        Produtos vendidos
                    </Typography>
                    {produtosVendidos.length > 0 ? (
                        <TableContainer sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Quantidade</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Valor total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {produtosVendidos.map(({ descricao, quantidade, valorTotal }) => (
                                        <TableRow key={descricao}>
                                            <TableCell>{descricao}</TableCell>
                                            <TableCell align="center">{quantidade}</TableCell>
                                            <TableCell align="right">{real(valorTotal)}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                            {produtosVendidos.reduce((acc, p) => acc + p.quantidade, 0)}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                                            {real(produtosVendidos.reduce((acc, p) => acc + p.valorTotal, 0))}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Nenhum produto registrado
                        </Typography>
                    )}

                    {/* 6. Taxas de entrega */}
                    {taxasEntrega.length > 0 && (
                        <>
                            <Typography variant="h6" fontWeight="bold" sx={{ borderBottom: '2px solid #000', mb: 1 }}>
                                Taxas de entrega
                            </Typography>
                            <TableContainer sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="center">Quantidade</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Valor total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {taxasEntrega.map(({ descricao, quantidade, valorTotal }) => (
                                            <TableRow key={descricao}>
                                                <TableCell>{descricao}</TableCell>
                                                <TableCell align="center">{quantidade}</TableCell>
                                                <TableCell align="right">{real(valorTotal)}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="right">
                                                {real(taxasEntrega.reduce((acc, t) => acc + t.valorTotal, 0))}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    {/* 7. Contas manuais */}
                    {contasManuais.length > 0 && (
                        <>
                            <Typography variant="h6" fontWeight="bold" sx={{ borderBottom: '2px solid #000', mb: 1 }}>
                                Contas manuais
                            </Typography>
                            <TableContainer sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Data / Hora</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="center">Tipo</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Valor</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {contasManuais.map((c, i) => (
                                            <TableRow key={c.uuid ?? i}>
                                                <TableCell>{c.descricao}</TableCell>
                                                <TableCell>{dataHoraBr(c.dataHora)}</TableCell>
                                                <TableCell align="center">
                                                    {c.tipo === 'pagar' ? 'Saída' : 'Entrada'}
                                                </TableCell>
                                                <TableCell align="right">{real(c.valor)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    {/* Rodapé */}
                    <Divider sx={{ mt: 2, mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                        {nomeEstabelecimento} — Gerado em {dataHoraBr(new Date().toISOString())}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
